import { GridColDef, GridToolbarContainer } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { UserKeys, getAppLogs } from '../../../../api/users-api';
import { AppLog } from '../../../../../types';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  DateRange,
  PickersShortcutsItem,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { Messages } from '../../../../utils/message-constants';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';

import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import History from '@mui/icons-material/History';
import Refresh from '@mui/icons-material/Refresh';

const columns: GridColDef<AppLog>[] = [
  {
    field: 'createdDate',
    headerName: 'Date',
    editable: false,
    sortable: false,
    filterable: false,
    width: 200,
    type: 'dateTime',
    valueGetter: (_value, row) => {
      if (row.createdDate === 0) return '';
      return new Date(row.createdDate);
    },
    valueFormatter: (value) => {
      if (value === '') return '';
      return new Date(value as string).toLocaleString();
    },
  },
  {
    field: 'performingUser',
    headerName: 'Performing User',
    editable: false,
    sortable: false,
    filterable: false,
    width: 300,
  },
  {
    field: 'action',
    headerName: 'Action',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 1,
  },
];

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Last Hour',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(1, 'hour'), today];
    },
  },
  {
    label: 'Last 24 Hours',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(1, 'day'), today];
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(30, 'day'), today];
    },
  },
  { label: 'Clear', getValue: () => [null, null] },
];

export default function HistoryPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const queryClient = useQueryClient();

  const { administrationRoles } = useUserSettings();

  const pageSize = 200;
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    dateRange: DateRange<Dayjs>;
  }>({
    pageSize: pageSize,
    page: 0,
    cursor: '',
    dateRange: [
      dayjs().subtract(7, 'day').startOf('day'),
      dayjs().endOf('day'),
    ],
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.AppLogs,
      userEmail,
      paginationModel.page,
      paginationModel.dateRange[0]?.toDate().getTime() || 0,
      paginationModel.dateRange[1]?.toDate().getTime() || 0,
    ],
    queryFn: () =>
      getAppLogs(
        userEmail,
        paginationModel.cursor || '',
        paginationModel.dateRange[0]?.toDate().getTime() || 0,
        paginationModel.dateRange[1]?.toDate().getTime() || 0
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: keepPreviousData,
    enabled: isAbleToSeeUIElement(
      administrationRoles,
      AdministrationRoles.ViewAppLogs.value
    ),
    meta: {
      errorMessage: Messages.Errors.Network.Loading("this user's app logs"),
    },
  });

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{ display: 'flex', justifyContent: 'space-between', p: 1, py: 0.5 }}
      >
        <Stack flexDirection="row" gap={1} alignItems="center">
          {!isAbleToSeeUIElement(
            administrationRoles,
            AdministrationRoles.ViewAppLogs.value
          ) ? (
            <Alert severity="error">
              The "View App Logs" permission is required to view App Logs.
            </Alert>
          ) : (
            <>
              <DateRangePicker
                sx={{ width: 250 }}
                label={'Date Range'}
                value={paginationModel.dateRange}
                calendars={2}
                onChange={(newValue) => {
                  setPaginationModel({
                    pageSize: pageSize,
                    page: 0,
                    cursor: '',
                    dateRange: newValue,
                  });
                }}
                slotProps={{
                  shortcuts: {
                    items: shortcutsItems,
                  },
                  actionBar: { actions: [] },
                }}
                slots={{ field: SingleInputDateRangeField }}
              />
              <Alert severity="info">
                App logs are filtered to show actions that occured on this user.
              </Alert>
            </>
          )}
        </Stack>

        <Tooltip arrow title="Refresh">
          <IconButton
            onClick={() => {
              setPaginationModel({
                ...paginationModel,
                pageSize: pageSize,
                page: 0,
                cursor: '',
              });

              void queryClient.invalidateQueries({
                queryKey: [UserKeys.AppLogs, userEmail],
              });
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </GridToolbarContainer>
    );
  }

  const parsedData = data?.results.map((result) => {
    return {
      ...result,
      action: result.action
        ? result.action.replace('&#39;', "'").replace('&quot;', '"')
        : '',
      permission: result.permission
        ? result.permission.replace('&#39;', "'").replace('&quot;', '"')
        : '',
      id: uuidv4(),
    };
  });

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays(
            'History',
            <History />,
            paginationModel.dateRange[0] !== null ||
              paginationModel.dateRange[1] !== null
              ? 'Try updating your search or filters.'
              : ''
          ),
        }}
        disableColumnMenu
        data={data}
        rows={parsedData || []}
        columns={columns}
        sx={{
          '.MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-toolbar': {
            padding: '4px 8px',
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: pageSize, page: 0 },
          },
        }}
        toolbarQuickFilterWidth={250}
        loading={isLoading || isFetching}
        disableColumnSelector
        disableRowSelectionOnClick
        pageSizeOptions={[pageSize]}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          setPaginationModel({
            ...newModel,
            cursor: data?.cursor || '',
            dateRange: paginationModel.dateRange,
          });
        }}
      />
    </>
  );
}
